// src/pages/cadastros/Defeitos/DefeitosPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useSortableTable, SortableHeader } from "../../../hooks/useSortableTable";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import CardListMobile from "../../../templates/mobile/CardListMobile";
import { temNivelMinimo, pode } from "../../../utils/permissoes";
import { NIVEIS } from "../../../config/niveis";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import { Can } from "../../../components/Can";
import { filtrarPorEmpresa } from "../../../utils/empresaScope";

import { db } from "../../../config/firebase";
import { collection, getDocs, query, where, deleteDoc, doc } from "firebase/firestore";

const camposFiltro = [
  { value: "nome", label: "Nome" },
  { value: "empresaNome", label: "Empresa" },
  { value: "setorNome", label: "Setor" },
];

function DefeitosPage() {
  const { modoGlobal, empresaAtual } = useEmpresa();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const canCriar = temNivelMinimo(usuarioLogado, NIVEIS.ADMIN);
  const canEditar = temNivelMinimo(usuarioLogado, NIVEIS.ADMIN);
  const canExcluir = temNivelMinimo(usuarioLogado, NIVEIS.ADMIN);
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [defeitos, setDefeitos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");

  useEffect(() => {
    async function carregar() {
      if (!usuarioLogado?.grupoId) {
        setErro(
          "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
        );
        setCarregando(false);
        return;
      }

      try {
        const defeitosRef = collection(db, "defeitos");
        const q = query(defeitosRef, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        lista.sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""))
        );

        setDefeitos(lista);
      } catch (e) {
        console.error("Erro ao carregar defeitos:", e);
        setErro("Erro ao carregar defeitos. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregar();
  }, [usuarioLogado]);

  const defeitosFiltrados = defeitos.filter((def) => {
    if (!textoFiltro.trim()) return true;
    const valor = String(def[campoFiltro] || "").toLowerCase();
    return valor.includes(textoFiltro.toLowerCase());
  });

  const { sortedData, requestSort, getSortDirection } = useSortableTable(defeitosFiltrados);

  function irParaNovo() {
    navigate("/cadastros/defeitos/novo");
  }

  function irParaPorMaquina() {
    navigate("/cadastros/defeitos/por-maquina");
  }

  function irParaEditar(id) {
    navigate(`/cadastros/defeitos/editar/${id}`);
  }

  async function handleExcluir(id) {
    if (!window.confirm("Tem certeza que deseja excluir este defeito?")) return;

    try {
      await deleteDoc(doc(db, "defeitos", id));
      setDefeitos((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      console.error("Erro ao excluir defeito:", e);
      setErro("Erro ao excluir defeito.");
    }
  }

  if (isMobile) {
    const mobileItems = defeitos.map(def => ({
      id: def.id,
      nome: def.nome || "(sem nome)",
      subtitulo: def.empresaNome || "Empresa não definida",
      detalhes: [
        { icone: "🏭", label: "Setor", valor: def.setorNome || "-" },
        { icone: "⚙️", label: "Máquinas", valor: def.maquinasNomes?.join(", ") || "-" },
      ]
    }));

    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Defeitos"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <CardListMobile
          items={mobileItems}
          carregando={carregando}
          erro={erro}
          searchPlaceholder="Buscar defeitos..."
          onEditar={irParaEditar}
          onExcluir={canExcluir ? handleExcluir : undefined}
          getSearchText={(item) => item.nome}
          emptyMessage="Nenhum defeito cadastrado"
          customActions={
            <>
              {canCriar && (
<button
                type="button"
                onClick={irParaNovo}
                className="m-btn-pill m-btn-primary"
              >
                + Novo Defeito
              </button>
)}
              <button
                type="button"
                onClick={irParaPorMaquina}
                className="m-btn-pill m-btn-secondary"
              >
                Por Máquina
              </button>
            </>
          }
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Novo Defeito"
      subtitulo=""
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Defeitos cadastrados</h3>
            <p>Lista de defeitos por empresa / setor / máquinas.</p>
          </div>

          <div className="pc-table-header-actions">
            <button
              type="button"
              className="pc-btn-secundario"
              onClick={irParaPorMaquina}
            >
              Defeitos por máquina
            </button>
            {canCriar && (
              <Can modulo="cadastros" recurso="defeitos" acao="form">
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={irParaNovo}
                >
                  + Novo Defeito
                </button>
              </Can>
            )}
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={irParaNovo}
          placeholder="Filtrar defeitos..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div className="pc-alert pc-alert-erro">
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando defeitos...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum defeito encontrado.</p>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <SortableHeader label="Nome" sortKey="nome" onSort={requestSort} sortDirection={getSortDirection("nome")} />
                  <SortableHeader label="Empresa" sortKey="empresaNome" onSort={requestSort} sortDirection={getSortDirection("empresaNome")} />
                  <SortableHeader label="Setor" sortKey="setorNome" onSort={requestSort} sortDirection={getSortDirection("setorNome")} />
                  <th>Máquinas</th>
                  <th>Observações</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((def) => (
                  <tr key={def.id}>
                    <td>{def.nome || "(sem nome)"}</td>
                    <td>{def.empresaNome || "-"}</td>
                    <td>{def.setorNome || "-"}</td>
                    <td>
                      {def.maquinasNomes && def.maquinasNomes.length > 0
                        ? def.maquinasNomes.length > 2 
                          ? `${def.maquinasNomes.slice(0, 2).join(", ")} +${def.maquinasNomes.length - 2}`
                          : def.maquinasNomes.join(", ")
                        : "-"}
                    </td>
                    <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {def.observacoes || "-"}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {canEditar && (
<button
                        type="button"
                        title="Editar defeito"
                        onClick={() => irParaEditar(def.id)}
                        style={{
                          border: "none",
                          
                          cursor: "pointer",
                          fontSize: 18,
                          marginRight: 8,
                        }}
                      >
                        ✏️
                      </button>
)}
                      {canExcluir && (
<button
                        type="button"
                        title="Apagar defeito"
                        onClick={() => handleExcluir(def.id)}
                        style={{
                          border: "none",
                          
                          cursor: "pointer",
                          fontSize: 18,
                        }}
                      >
                        🗑️
                      </button>
)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default DefeitosPage;
